import { useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { Address, parseUnits } from "viem"
import { useAccount, useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import CreateIncentiveAlertDialog from "@/components/create-incentive-alert-dialog"
import { Switch } from "@/components/switch"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip"
import { voterAbi } from "@/domains/abis"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MultilineMessageBox } from "@/domains/common/components/multi-line-message-box"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { LcAddress } from "@/domains/core"
import { BodyXs } from "@/domains/design/components/body-xs"
import { ChevronDownIcon, HelpCircleIcon, IncentiveIcon, InfoSquareIcon } from "@/domains/icons"
import { createBribeInteraction, getCorrectBribeAddress } from "@/domains/interactions"
import { cn, formatCurrency } from "@/domains/library"
import { SimpleMinimalPoolDialog } from "@/domains/minimal-pool/components/minimal-pool-dialog/simple-minimal-pool-dialog"
import { MinimalPoolStore } from "@/domains/minimal-pool/minimal-pool-store"
import { useCreateBribeLens } from "@/domains/on-chain"
import { TokenStore } from "@/domains/tokens"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"
import { TokenInputCard } from "@/domains/tokens/components/token-input-card"
import { SCALE } from "@/domains/tokens/static-tokens"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useNativeToken } from "@/hooks/use-native-token"
import { useVestedToken } from "@/hooks/use-vested-token"
import { useWrappedToken } from "@/hooks/use-wrapped-token"
import useEventEmitter from "@/hooks/useEventEmitter"

const Incentives = observer(() => {
  const account = useAccount()
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const vestedToken = useVestedToken()
  const governanceToken = useGovernanceToken()
  const nativeToken = useNativeToken()
  const wrappedNative = useWrappedToken()
  const scaleApp = useScaleApplication()
  const incentiveStore = scaleApp.Pages.Incentives

  // Get minimal pools with gauges
  const poolsWithGauges = useMemo(() => {
    return scaleApp.MinimalPools.Repository.getAll().filter((pool) => pool.hasGauge)
  }, [scaleApp.MinimalPools.Repository.totalPools])

  // Get tokens from repository
  const tokens = useMemo(() => {
    return scaleApp.Tokens.Repository.getAll()
  }, [scaleApp.Tokens.Repository.totalTokens])

  // Get incentiveType from store
  const incentiveType = incentiveStore.incentiveType

  const filterIncentiveToken = (token: TokenStore): boolean => {
    if (incentiveType === "lp") {
      // For LP incentives, only allow governance token (SCALE)
      return token.address.equals(SCALE.address)
    }
    return Boolean(token.isIncentiveToken)
  }

  // Get governance token (SCALE) from repository
  const governanceTokenStore = useMemo(() => {
    return scaleApp.Tokens.Repository.find(SCALE.address)
  }, [scaleApp.Tokens.Repository.totalTokens])

  // Resolve selectedToken from tokenAddress in store
  const selectedToken = useMemo(() => {
    const tokenAddress = incentiveStore.tokenAddress
    if (!tokenAddress) {
      // Default to SCALE if no token address
      return scaleApp.Tokens.Repository.find(SCALE.address) ?? governanceTokenStore ?? undefined
    }
    const token = scaleApp.Tokens.Repository.find(new LcAddress(tokenAddress))
    // Fallback to SCALE if token not found
    return token ?? scaleApp.Tokens.Repository.find(SCALE.address) ?? governanceTokenStore ?? undefined
  }, [incentiveStore.tokenAddress, scaleApp.Tokens.Repository, governanceTokenStore])

  // Resolve selectedPool from pairAddress in store
  const selectedPool = useMemo(() => {
    const pairAddress = incentiveStore.pairAddress
    if (!pairAddress) {
      return null
    }
    const pairAddressLower = pairAddress.toLowerCase()
    return poolsWithGauges.find((pool) => pool.address.toString().toLowerCase() === pairAddressLower) ?? null
  }, [incentiveStore.pairAddress, poolsWithGauges])

  // @todo MIGRATE: useEffect data fetching to MobX store with OrchestratedQuery
  // Log whenever selected pool changes
  useEffect(() => {
    if (selectedPool) {
      const isCLPool = selectedPool?.tags?.includes("cl-farm") || selectedPool?.extendedType?.startsWith("CL-")

      // Log custom gauge check
      if (selectedPool.gaugeAddress) {
        const client = scaleApp.getPublicClient()
        if (client) {
          client
            .readContract({
              address: chainConfig.equalizer.contracts.voter,
              abi: voterAbi,
              functionName: "customGauges",
              args: [selectedPool.gaugeAddress.toViemAddress()],
            })
            .then((customGauge) => {
              console.log(
                customGauge === "0x0000000000000000000000000000000000000000"
                  ? "No custom gauge found"
                  : `Custom gauge found: ${customGauge}`,
              )
            })
        }
      }
    }
  }, [selectedPool, config, chainConfig])

  // Set default LP token when incentiveType changes to "lp" (always SCALE/governance token)
  useEffect(() => {
    if (incentiveType === "lp") {
      const governanceTokenStore = scaleApp.Tokens.Repository.find(SCALE.address)
      if (governanceTokenStore) {
        incentiveStore.setTokenAddressFromUi(governanceTokenStore.address.toString())
      }
    }
  }, [incentiveType, scaleApp.Tokens.Repository, incentiveStore])

  const [amount, setAmount] = useState("0")
  const [isPoolDialogOpen, setIsPoolDialogOpen] = useState(false)
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false)

  // Get bribe address for vote bribes (needs to be fetched from contract)
  const [bribeAddress, setBribeAddress] = useState<Address | undefined>(undefined)

  // @todo MIGRATE: useEffect data fetching to MobX store with OrchestratedQuery
  useEffect(() => {
    if (selectedPool?.gaugeAddress && incentiveType === "vote") {
      const client = scaleApp.getPublicClient()
      if (client) {
        client
          .readContract({
            address: chainConfig.equalizer.contracts.voter,
            abi: voterAbi,
            functionName: "bribes",
            args: [selectedPool.gaugeAddress.toViemAddress()],
          })
          .then((bribeAddr) => {
            if (bribeAddr && bribeAddr !== "0x0000000000000000000000000000000000000000") {
              setBribeAddress(bribeAddr as Address)
            }
          })
          .catch((error) => {
            console.error("Failed to fetch bribe address:", error)
            setBribeAddress(undefined)
          })
      }
    } else {
      setBribeAddress(undefined)
    }
  }, [selectedPool, incentiveType, config, chainConfig])

  const lens = useCreateBribeLens({
    account: account.address!,
    chainId: chainConfig.chainId,
    config,
    token: selectedToken
      ? {
          address: selectedToken.address.toViemAddress(),
          symbol: selectedToken.symbol,
          decimals: selectedToken.decimals,
        }
      : (governanceToken as any),
    bribeAddress: bribeAddress ?? ("0x0000000000000000000000000000000000000000" as Address),
    bribeType: incentiveType === "lp" ? "gauge" : incentiveType,
    selectedGauge: selectedPool?.gaugeAddress?.toViemAddress(),
    refetchInterval: 5000,
  })

  const bribeTokenBalance = lens.accountBalanceToken.value

  // Get quick select tokens from store
  const quickSelectTokens = scaleApp.Tokens.QuickSelect.tokens

  const handleCreateIncentive = async () => {
    if (!selectedPool || !selectedToken) {
      return
    }

    try {
      const correctBribeAddress = await getCorrectBribeAddress(
        incentiveType,
        selectedPool,
        config,
        chainConfig.equalizer.contracts.voter,
      )

      const result = await createBribeInteraction({
        amount: parseUnits(amount, selectedToken.decimals),
        account: account.address!,
        chainId: chainConfig.chainId,
        config,
        selectedToken: {
          address: selectedToken.address.toViemAddress(),
          symbol: selectedToken.symbol,
          decimals: selectedToken.decimals,
        },
        bribeAddress: correctBribeAddress,
        emitter,
        lens,
        approvalMode: scaleApp?.Settings.approvalMode,
        bribeType: incentiveType === "lp" ? "lp" : "vote",
      })

      if (result && result.success) {
        emitter.emit("toast", {
          variant: "success",
          title: "Incentive Created",
          description: `Successfully created ${incentiveType} incentive for ${selectedPool.displayName}`,
        })
        setIsAlertDialogOpen(false)
      }
    } catch (error) {
      console.error(error)
      emitter.emit("toast", {
        variant: "error",
        title: "Error Creating Incentive",
        description: "Failed to create incentive. Please try again.",
      })
    }
  }

  const title = "Incentivize"

  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title="Incentivize" />
        <div className="flex flex-col gap-4 lg:flex-row">
          <Card.Container className="max-w-xl">
            <Card.Header>
              <Card.HeaderIconContainer>
                <IncentiveIcon size={24} className="text-primary-500 " />
              </Card.HeaderIconContainer>
              <div className="flex w-full flex-col">
                <Card.HeaderTitle className="flex items-center gap-2">
                  Incentivize {incentiveType === "vote" ? "Voters" : "Liquidity Providers"}
                  <TooltipProvider>
                    <Tooltip delayDuration={100}>
                      <TooltipTrigger asChild>
                        <HelpCircleIcon size={16} className="cursor-pointer text-t200" />
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={5} className="p-5">
                        {incentiveType === "vote" ? (
                          <p>
                            If you are a project and want to incentivize voter participation,
                            <br />
                            you can create an incentive for your project to encourage more votes.
                          </p>
                        ) : (
                          <p>
                            If you are a project and want to incentivize liquidity provision,
                            <br />
                            you can add more rewards to boost APRs on your pool.
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Card.HeaderTitle>
                <Card.HeaderDescription>
                  Incentivize {incentiveType === "vote" ? "voting" : "farming"} on a specific liquidity pair by
                  incentivizing {incentiveType === "vote" ? `${vestedToken.symbol} holders` : "liquidity providers"}
                </Card.HeaderDescription>
              </div>
            </Card.Header>
            <Card.Separator />

            <div className="flex items-center gap-3">
              <p className="text-t200">Vote Incentive</p>
              <Switch
                id="lp-incentive"
                checked={incentiveType === "lp"}
                onCheckedChange={(checked) => incentiveStore.setIncentiveTypeFromUi(checked ? "lp" : "vote")}
              />
              <p className="text-t200">LP Incentive</p>
            </div>

            <div className="mb-4">
              <div className="mb-1 text-sm text-t200">{incentiveType === "vote" ? "Pair" : "Pair"}</div>
              <div
                className={cn(
                  "flex w-full cursor-pointer flex-row items-center justify-between rounded-xl",
                  "border border-p400-light bg-p500",
                  "text-base text-t200",
                  "px-4 py-2",
                )}
                onClick={() => setIsPoolDialogOpen(true)}
              >
                {selectedPool ? (
                  <div className={"flex w-full items-center justify-start gap-2"}>
                    <DisplayTokens tokens={[selectedPool.token0, selectedPool.token1]} tokenSize={32} />
                    <div className="flex flex-col items-start">
                      <div className="text-xs text-t100">{selectedPool.displayName}</div>
                      <div className="text-xs text-t200">{selectedPool.displayType}</div>
                    </div>
                  </div>
                ) : (
                  `Select a ${incentiveType === "vote" ? "pair" : "pair"} from the list`
                )}
                <ChevronDownIcon size={20} className="ml-2 shrink-0 text-t100 opacity-80" />
              </div>

              <SimpleMinimalPoolDialog
                isOpen={isPoolDialogOpen}
                pools={poolsWithGauges}
                governanceToken={governanceToken}
                nativeToken={nativeToken}
                wrappedNativeToken={wrappedNative.symbol}
                onClose={() => setIsPoolDialogOpen(false)}
                onPoolSelect={(pool) => {
                  if (pool) {
                    incentiveStore.setPairAddressFromUi(pool.address.toString())
                  } else {
                    incentiveStore.setPairAddressFromUi("")
                  }
                  setIsPoolDialogOpen(false)
                }}
              />
            </div>
            <div>
              <div className="mb-1 text-sm text-t200">Incentive token</div>
              <TokenInputCard
                amount={amount}
                showTokenSymbol={false}
                showTokenSelectable={true}
                token={selectedToken ?? null}
                tokens={incentiveType === "lp" ? tokens.filter(filterIncentiveToken) : tokens}
                onTokenAmountChange={setAmount}
                onTokenSelect={(token) => {
                  if (token) {
                    incentiveStore.setTokenAddressFromUi(token.address.toString())
                  } else {
                    // Reset to default (SCALE)
                    incentiveStore.setTokenAddressFromUi(SCALE.address.toString())
                  }
                }}
                placeholder="0"
              />
            </div>
            <div>
              {selectedToken && selectedPool ? (
                <div className="flex flex-col gap-8">
                  <CreationMessage
                    selectedToken={selectedToken}
                    selectedPool={selectedPool}
                    amount={amount}
                    incentiveType={incentiveType}
                  />

                  <div className="flex flex-row justify-end">
                    <Button variant="primary" onClick={() => setIsAlertDialogOpen(true)}>
                      Create incentive
                    </Button>
                    <CreateIncentiveAlertDialog
                      isOpen={isAlertDialogOpen}
                      onClose={() => setIsAlertDialogOpen(false)}
                      amount={amount}
                      selectedToken={{
                        address: selectedToken.address.toViemAddress(),
                        symbol: selectedToken.symbol,
                        decimals: selectedToken.decimals,
                      }}
                      handleCreateIncentive={handleCreateIncentive}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </Card.Container>
        </div>
      </PageContainer>
    </TooltipProvider>
  )
})

const CreationMessage = ({
  selectedToken,
  selectedPool,
  amount,
  incentiveType,
}: {
  selectedToken: TokenStore
  selectedPool: MinimalPoolStore
  amount: string
  incentiveType: "vote" | "lp"
}) => {
  if (incentiveType === "vote") {
    return (
      <MultilineMessageBox icon={<InfoSquareIcon size={20} />} variant="info" size="sm">
        <BodyXs className="text-t100">
          You are creating an incentive of {formatCurrency(amount, 4)} {selectedToken.symbol}
          to incentivize votes on the <span className="whitespace-nowrap">{selectedPool.displayName}</span>{" "}
          {selectedPool.displayType} Pool
        </BodyXs>
      </MultilineMessageBox>
    )
  }
  return (
    <MultilineMessageBox icon={<InfoSquareIcon size={20} />} variant="info" size="sm">
      <BodyXs className="text-t100">
        You are creating an incentive of {formatCurrency(amount, 4)} {selectedToken.symbol}
        to incentivize liquidity provision on the <span className="whitespace-nowrap">
          {selectedPool.displayName}
        </span>{" "}
        {selectedPool.displayType} Pool
      </BodyXs>
    </MultilineMessageBox>
  )
}

export default Incentives
